import { useMemo, useState, type ReactNode } from "react";
import type { LobbyPlayer } from "../game/online-connection";
import { fa } from "../i18n/fa";
import { useSession } from "../store/context";
import { AvatarRing, Identity, Pill, Sheet } from "./social-ui";
import { displayName } from "./profile-ui";

/**
 * FEATURE 3 — the mid-game friend picker: the table OWNER tapped a BOT chair and chooses a friend
 * to invite into it. Picking one calls `onPick(userId)` (the container mints the seat-fill token
 * and creates the invite) and closes the sheet. Deliberately light — a quick pick, not the
 * pre-start invite panel's full state machine (that lives in {@link InviteFriendList}).
 *
 * Friends already seated at THIS table are hidden (they can't fill another chair here). A search
 * box appears past a handful of friends.
 */
export function SeatInviteSheet({
  players,
  onPick,
  onClose,
}: {
  players?: readonly LobbyPlayer[];
  onPick: (userId: string) => void;
  onClose: () => void;
}): ReactNode {
  const friends = useSession((s) => s.friends);
  const presence = useSession((s) => s.presence);
  const [query, setQuery] = useState("");

  // Exclude friends who are already sitting at this table (any seat).
  const seatedIds = useMemo(
    () => new Set((players ?? []).map((p) => p.user?.userId).filter(Boolean) as string[]),
    [players],
  );
  const needle = query.trim().toLowerCase();
  const candidates = useMemo(
    () =>
      friends.filter(
        (f) =>
          !seatedIds.has(f.userId) &&
          (needle === "" ||
            displayName(f).toLowerCase().includes(needle) ||
            (f.appUsername ?? "").toLowerCase().includes(needle)),
      ),
    [friends, seatedIds, needle],
  );

  return (
    <Sheet label={fa.online.seatInviteTitle()} onClose={onClose}>
      <p className="mb-3 text-caption text-on-surface-variant">{fa.online.seatInviteHint}</p>
      {friends.length > 6 ? (
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={fa.friends.searchPlaceholder}
          className="mb-3 w-full rounded-lg border border-outline-variant bg-surface-container px-3 py-2 text-body-md text-on-surface outline-none placeholder:text-on-surface-variant"
        />
      ) : null}
      <div className="flex max-h-[46vh] flex-col gap-1.5 overflow-y-auto overflow-x-hidden">
        {candidates.length === 0 ? (
          <p className="py-6 text-center text-caption text-on-surface-variant">
            {fa.online.seatInviteEmpty}
          </p>
        ) : (
          candidates.map((friend) => (
            <div
              key={friend.userId}
              className="flex min-w-0 items-center gap-2 rounded-lg border border-outline-variant/60 bg-surface-container-low px-2 py-1.5"
            >
              <AvatarRing
                avatarId={friend.avatarId}
                avatarPhotoId={friend.avatarPhotoId}
                size={30}
                status={presence[friend.userId]}
              />
              <div className="min-w-0 flex-1">
                <Identity user={friend} />
              </div>
              <Pill
                label={fa.invite.invite}
                icon="invite"
                variant="primary"
                onClick={() => {
                  onPick(friend.userId);
                  onClose();
                }}
              />
            </div>
          ))
        )}
      </div>
    </Sheet>
  );
}
